// @ts-check

/**
 * Error codes used throughout the chat provider
 * Mirrors backend error codes for consistency
 * @enum {string}
 */
export const ErrorCodes = {
  // Authentication & Authorization Errors
  INVALID_TOKEN: 'INVALID_TOKEN',
  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
  PERMISSION_DENIED: 'PERMISSION_DENIED',

  // Resource Errors
  NOT_FOUND: 'NOT_FOUND',
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  SESSION_NOT_FOUND: 'SESSION_NOT_FOUND',
  MESSAGE_NOT_FOUND: 'MESSAGE_NOT_FOUND',
  PROJECT_NOT_FOUND: 'PROJECT_NOT_FOUND',
  REPORT_NOT_FOUND: 'REPORT_NOT_FOUND',

  // Validation Errors
  MISSING_PARAMETER: 'MISSING_PARAMETER',
  INVALID_PARAMETER: 'INVALID_PARAMETER',
  INVALID_OPERATION: 'INVALID_OPERATION',

  // Business Logic Errors
  INSUFFICIENT_FUNDS: 'INSUFFICIENT_FUNDS',
  ACTIVE_STREAM_EXISTS: 'ACTIVE_STREAM_EXISTS',

  // GitHub Service Errors
  GITHUB_API_ERROR: 'GITHUB_API_ERROR',
  GITHUB_RATE_LIMIT_EXCEEDED: 'GITHUB_RATE_LIMIT_EXCEEDED',
  GITHUB_BRANCH_NOT_FOUND: 'GITHUB_BRANCH_NOT_FOUND',
  GITHUB_AUTH_FAILED: 'GITHUB_AUTH_FAILED',
  GITHUB_RESOURCE_NOT_FOUND: 'GITHUB_RESOURCE_NOT_FOUND',
  GITHUB_CONFLICT: 'GITHUB_CONFLICT',

  // Server Errors
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',

  // Network Errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  REQUEST_ABORTED: 'REQUEST_ABORTED',
  REQUEST_TIMEOUT: 'REQUEST_TIMEOUT',

  // Stream Errors
  STREAM_ERROR: 'STREAM_ERROR',
  STREAM_DISCONNECTED: 'STREAM_DISCONNECTED',

  // Unknown Errors
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
};

/**
 * HTTP status code to error code mapping
 */
const HTTP_STATUS_TO_ERROR_CODE = {
  400: ErrorCodes.INVALID_PARAMETER,
  401: ErrorCodes.INVALID_TOKEN,
  403: ErrorCodes.PERMISSION_DENIED,
  404: ErrorCodes.RESOURCE_NOT_FOUND,
  500: ErrorCodes.INTERNAL_SERVER_ERROR,
  503: ErrorCodes.SERVICE_UNAVAILABLE,
};

/**
 * Custom error class for chat provider errors
 */
export class ChatProviderError extends Error {
  /**
   * @param {string} code - Error code from ErrorCodes enum
   * @param {string} message - Human-readable error message
   * @param {string} [details] - Detailed error description
   * @param {any} [cause] - Original error cause
   */
  constructor(code, message, details, cause) {
    super(message);
    this.name = 'ChatProviderError';
    this.code = code;
    this.details = details;
    this.timestamp = new Date().toISOString();
    this.cause = cause;
  }

  /**
   * Create error response object
   */
  toJSON() {
    return {
      error: {
        code: this.code,
        message: this.message,
        details: this.details,
        timestamp: this.timestamp,
      }
    };
  }
}

/**
 * Parse error from backend response
 * @param {any} responseJson - Response JSON from backend
 * @param {number} [status] - HTTP status code
 * @returns {ChatProviderError}
 */
export function parseBackendError(responseJson, status) {
  // Backend uses standardized error format
  if (responseJson?.error) {
    const { code, message, details } = responseJson.error;
    return new ChatProviderError(
      code || ErrorCodes.UNKNOWN_ERROR,
      message || 'An error occurred',
      details,
      responseJson
    );
  }

  // Fallback for non-standardized errors
  const code = (status && HTTP_STATUS_TO_ERROR_CODE[status]) || ErrorCodes.UNKNOWN_ERROR;
  const message = responseJson?.message || 'An error occurred';

  return new ChatProviderError(code, message, undefined, responseJson);
}

/**
 * Handle fetch errors (network, abort, etc.)
 * @param {Error} error - Original error
 * @returns {ChatProviderError}
 */
export function handleFetchError(error) {
  if (error.name === 'AbortError') {
    return new ChatProviderError(
      ErrorCodes.REQUEST_ABORTED,
      'Request was aborted',
      undefined,
      error
    );
  }

  if (error.message?.includes('fetch') || error.message?.includes('network')) {
    return new ChatProviderError(
      ErrorCodes.NETWORK_ERROR,
      'Network error occurred',
      error.message,
      error
    );
  }

  // If it's already a ChatProviderError, return it
  if (error instanceof ChatProviderError) {
    return error;
  }

  // Unknown error
  return new ChatProviderError(
    ErrorCodes.UNKNOWN_ERROR,
    error.message || 'An unknown error occurred',
    undefined,
    error
  );
}

/**
 * Helper function to create a standardized error response
 * @param {string} code - Error code from ErrorCodes enum
 * @param {string} message - Human-readable error message
 * @param {string} [details] - Detailed error description
 * @returns {ChatProviderError}
 */
export function createError(code, message, details) {
  return new ChatProviderError(code, message, details);
}

/**
 * Check if error is a specific type
 * @param {Error} error
 * @param {string} code
 * @returns {boolean}
 */
export function isErrorCode(error, code) {
  return error instanceof ChatProviderError && error.code === code;
}

/**
 * Check if error is retryable
 * @param {Error} error
 * @returns {boolean}
 */
export function isRetryableError(error) {
  if (!(error instanceof ChatProviderError)) return false;

  const retryableCodes = [
    ErrorCodes.NETWORK_ERROR,
    ErrorCodes.REQUEST_TIMEOUT,
    ErrorCodes.SERVICE_UNAVAILABLE,
    ErrorCodes.STREAM_DISCONNECTED,
  ];

  return retryableCodes.includes(error.code);
}
